import type {DataUploadNewTeacher} from 'common/helpers/dataUpload'
import {CalendarCheck, UserPlus} from 'lucide-react'
import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'

/**
 * What "Save and assign" gives the teachers, by what the file says of them: `lines` — class,
 * section and subject (allocated as written); `classes` — subjects only (their classes and
 * sections are asked next); `everything` — nothing to allocate here (subjects, classes and
 * sections are picked on Teacher Allocation).
 */
export type NewTeachersAssignScope = 'lines' | 'classes' | 'everything'

const ASSIGN_LABELS = {
  lines: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ASSIGN_LINES',
  classes: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ASSIGN_CLASSES',
  everything: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ASSIGN_ALL',
} satisfies Record<NewTeachersAssignScope, string>

interface Props {
  show: boolean
  teachers: readonly DataUploadNewTeacher[]
  /**
   * Whether the rest of the file imports without them: Cancel then just leaves their lines out.
   * Otherwise nothing can be imported until the names are fixed or the teachers saved.
   */
  canContinueWithout: boolean
  /**
   * `lines`: allocation lines wait on them (Teacher Allocation). `list`: the file also lists
   * them (Students); its own rows go in either way.
   */
  variant?: 'lines' | 'list'
  submitting?: boolean
  /**
   * The `key`s of the teachers the user ticked, exactly as the server sent them. With
   * `onSaveOnly`, this is "Save and assign": saved, and allocated as `assignScope` says.
   */
  onSave: (keys: string[]) => void
  /**
   * "Save teachers only": name and Teacher ID, nothing allocated. When given, the dialog offers
   * it beside "Save and assign"; otherwise it has the one Save.
   */
  onSaveOnly?: (keys: string[]) => void
  /** What "Save and assign" gives them (its label); `lines` when not said. */
  assignScope?: NewTeachersAssignScope
  /** "Don't save": closes; nothing is added. */
  onCancel: () => void
}

/**
 * "These teachers are not in your school yet. Save them as new teachers?" — for an allocation
 * upload whose lines name teachers the school has not saved. Each one is listed with how many
 * lines wait on them and a few of those lines, so the user can tell a new teacher from a
 * misspelt one. All are ticked to begin with; only the ticked ones are ever created.
 */
const NewTeachersModal: FC<Props> = ({
  show,
  teachers,
  canContinueWithout,
  variant = 'lines',
  submitting = false,
  onSave,
  onSaveOnly,
  assignScope = 'lines',
  onCancel,
}) => {
  const intl = useIntl()
  const [picked, setPicked] = useState<Set<string>>(() => new Set())

  // A new list (a new file, or a re-read that still names some) starts with every one ticked.
  useEffect(() => {
    if (show) setPicked(new Set(teachers.map((teacher) => teacher.key)))
  }, [show, teachers])

  /** The ticked teachers' keys, in the server's order. */
  const pickedKeys = () => teachers.map((teacher) => teacher.key).filter((key) => picked.has(key))

  const toggle = (key: string) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onCancel}
      centered
      size='lg'
      backdrop={submitting ? 'static' : undefined}
    >
      <Modal.Header closeButton={!submitting} className='border-0 pb-0'>
        <div>
          <Modal.Title className='fs-3'>
            {intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.TITLE'})}
          </Modal.Title>
          <p className='sheet-assign-hint mt-2 mb-0'>
            {intl.formatMessage({
              id:
                variant === 'list'
                  ? 'DATA_UPLOAD.NEW_TEACHERS.HINT_LIST'
                  : canContinueWithout
                    ? 'DATA_UPLOAD.NEW_TEACHERS.HINT_SOME'
                    : 'DATA_UPLOAD.NEW_TEACHERS.HINT_ALL',
            })}
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className='pt-4'>
        {teachers.map((teacher) => {
          const checked = picked.has(teacher.key)
          const inputId = `new-teacher-${teacher.key}`
          return (
            <div key={teacher.key} className={`sheet-assign-card${checked ? ' is-answered' : ''}`}>
              <div className='d-flex align-items-start gap-3'>
                <input
                  id={inputId}
                  type='checkbox'
                  className='form-check-input mt-1 flex-shrink-0'
                  checked={checked}
                  disabled={submitting}
                  onChange={() => toggle(teacher.key)}
                />
                <label htmlFor={inputId} className='flex-grow-1' style={{minWidth: 0}}>
                  <span className='d-flex flex-wrap align-items-center gap-2'>
                    <span className='sheet-assign-name'>{teacher.name}</span>
                    {teacher.employeeId && (
                      <span className='badge badge-light fs-8 fw-semibold'>
                        {teacher.employeeId}
                      </span>
                    )}
                    <span className='badge badge-light-primary fs-8 fw-semibold'>
                      {intl.formatMessage(
                        {id: 'DATA_UPLOAD.NEW_TEACHERS.LINES'},
                        {count: teacher.lines}
                      )}
                    </span>
                  </span>
                  {teacher.samples.length > 0 && (
                    <span className='d-block text-muted fs-7 mt-1'>
                      {teacher.samples.join(' · ')}
                    </span>
                  )}
                </label>
              </div>
            </div>
          )
        })}
      </Modal.Body>

      <Modal.Footer className='border-0 d-flex flex-wrap justify-content-between gap-3'>
        <span className='text-muted fs-8'>
          {intl.formatMessage({
            id: onSaveOnly
              ? variant === 'list'
                ? 'DATA_UPLOAD.NEW_TEACHERS.OPTIONS_NOTE_LIST'
                : 'DATA_UPLOAD.NEW_TEACHERS.OPTIONS_NOTE_LINES'
              : variant === 'list'
                ? 'DATA_UPLOAD.NEW_TEACHERS.DONT_SAVE_NOTE'
                : canContinueWithout
                  ? 'DATA_UPLOAD.NEW_TEACHERS.CANCEL_SOME'
                  : 'DATA_UPLOAD.NEW_TEACHERS.CANCEL_ALL',
          })}
        </span>
        <div className='d-flex flex-wrap gap-2 ms-auto'>
          <button type='button' className='btn btn-light' disabled={submitting} onClick={onCancel}>
            {intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.DONT_SAVE'})}
          </button>
          {onSaveOnly && (
            <button
              type='button'
              className='btn btn-light-primary d-inline-flex align-items-center gap-2'
              disabled={picked.size === 0 || submitting}
              onClick={() => onSaveOnly(pickedKeys())}
            >
              <UserPlus size={16} aria-hidden />
              {intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.SAVE_ONLY'}, {count: picked.size})}
            </button>
          )}
          <button
            type='button'
            className='btn btn-primary d-inline-flex align-items-center gap-2'
            disabled={picked.size === 0 || submitting}
            onClick={() => onSave(pickedKeys())}
          >
            {submitting ? (
              <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
            ) : onSaveOnly ? (
              <CalendarCheck size={16} aria-hidden />
            ) : (
              <UserPlus size={16} aria-hidden />
            )}
            {onSaveOnly
              ? intl.formatMessage({id: ASSIGN_LABELS[assignScope]})
              : intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.SAVE'}, {count: picked.size})}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default NewTeachersModal
